import React, { forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  warning?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
}

const FormFieldComponent = forwardRef<HTMLInputElement, FormFieldProps>(({
  label,
  error,
  warning,
  helperText,
  required = false,
  fullWidth = true,
  className = '',
  id,
  ...props
}, ref) => {
  const { isDark } = useTheme();
  const fieldId = id || `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const inputClasses = `
    w-full px-4 py-2 rounded-lg border transition-all duration-200
    ${isDark 
      ? 'bg-white/5 text-white placeholder-gray-400 border-white/20 focus:border-professional-blue-500 focus:bg-white/10' 
      : 'bg-white text-gray-900 placeholder-gray-500 border-gray-300 focus:border-professional-blue-500'
    }
    ${error 
      ? isDark ? 'border-red-400' : 'border-red-500'
      : warning 
        ? isDark ? 'border-yellow-400' : 'border-yellow-500'
        : ''
    }
    focus:outline-none focus:ring-2 focus:ring-professional-blue-500 focus:ring-offset-2
    ${isDark ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'}
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const labelClasses = `
    block text-sm font-medium mb-1
    ${isDark ? 'text-gray-300' : 'text-gray-700'}
  `;

  const helperClasses = `
    text-xs mt-1
    ${error 
      ? isDark ? 'text-red-400' : 'text-red-600'
      : warning
        ? isDark ? 'text-yellow-400' : 'text-yellow-600'
        : isDark ? 'text-gray-400' : 'text-gray-600'
    }
  `;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      <label htmlFor={fieldId} className={labelClasses}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <input
        ref={ref}
        id={fieldId}
        className={inputClasses}
        aria-invalid={!!error}
        aria-describedby={error || warning || helperText ? `${fieldId}-helper` : undefined}
        {...props}
      />
      
      {(error || warning || helperText) && (
        <p id={`${fieldId}-helper`} className={helperClasses}>
          {error || warning || helperText}
        </p>
      )}
    </div>
  );
});

FormFieldComponent.displayName = 'FormField';

export const FormField = React.memo(FormFieldComponent);

export default FormField;